import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowDownRight, ArrowUpRight, EyeOff, Loader2, Zap } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { formatNumberInput, parseDigits, todayISO } from "../lib/format";

export default function QuickInput({ onSaved }) {
  const { hasPerm } = useAuth();
  const canSpecial = hasPerm("create_special_expense");
  const canIncome = hasPerm("create_income") || hasPerm("create_income_imf");
  const canExpense = hasPerm("create_expense") || hasPerm("create_expense_imf") || canSpecial;
  const [type, setType] = useState(canExpense ? "expense" : "income");
  const [hidden, setHidden] = useState(false);
  const [book, setBook] = useState("resmi");

  const bookAllowed = (b) =>
    type === "income"
      ? b === "resmi" ? hasPerm("create_income") : hasPerm("create_income_imf")
      : b === "resmi" ? hasPerm("create_expense") : (hasPerm("create_expense_imf") || canSpecial);

  useEffect(() => {
    if (hidden) {
      setBook("imf");
      return;
    }
    if (!bookAllowed(book)) setBook(bookAllowed("resmi") ? "resmi" : "imf");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, hidden]);
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState(0);
  const [category, setCategory] = useState("");
  const [date, setDate] = useState(todayISO());
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/categories?type=${type}`)
      .then((r) => setCategories(r.data.filter((c) => c.active)))
      .catch(() => setCategories([]));
  }, [type]);

  const submit = async (e) => {
    e?.preventDefault();
    if (!description.trim()) {
      toast.error("Uraian wajib diisi");
      return;
    }
    if (!amount || amount <= 0) {
      toast.error("Nominal harus lebih besar dari 0");
      return;
    }
    setSaving(true);
    try {
      await api.post("/transactions", {
        date,
        type,
        description: description.trim(),
        category,
        amount,
        transaction_number: "",
        party: "",
        notes: "",
        hidden: type === "expense" ? hidden : false,
        book: type === "expense" && hidden ? "imf" : book,
      });
      toast.success(
        `${type === "income" ? "Pemasukan" : "Pengeluaran"} ${formatNumberInput(amount)} tersimpan${hidden && type === "expense" ? " (tersembunyi)" : ""}`
      );
      setDescription("");
      setAmount(0);
      setHidden(false);
      window.dispatchEvent(new Event("saldo:refresh"));
      onSaved?.();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  if (!canIncome && !canExpense) return null;

  return (
    <div
      data-testid="quick-input-bar"
      className="bg-[#1E293B] border border-slate-800 rounded-xl shadow-sm p-4"
    >
      <div className="flex items-center gap-2 mb-3">
        <Zap className="w-4 h-4 text-amber-500" />
        <p className="text-sm font-bold text-slate-100">Quick Input</p>
        <p className="text-xs text-slate-400">Catat pemasukan/pengeluaran dengan cepat</p>
      </div>
      <form onSubmit={submit} className="flex flex-wrap items-center gap-2">
        <div className="flex rounded-lg border border-slate-700 overflow-hidden">
          {canIncome && (
          <button
            type="button"
            data-testid="quick-input-type-income"
            onClick={() => { setType("income"); setCategory(""); }}
            className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold transition-colors ${
              type === "income" ? "bg-emerald-600 text-white" : "bg-[#1E293B] text-slate-400 hover:bg-emerald-500/15"
            }`}
          >
            <ArrowDownRight className="w-3.5 h-3.5" /> Pemasukan
          </button>
          )}
          {canExpense && (
          <button
            type="button"
            data-testid="quick-input-type-expense"
            onClick={() => { setType("expense"); setCategory(""); }}
            className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold transition-colors ${
              type === "expense" ? "bg-rose-600 text-white" : "bg-[#1E293B] text-slate-400 hover:bg-rose-500/15"
            }`}
          >
            <ArrowUpRight className="w-3.5 h-3.5" /> Pengeluaran
          </button>
          )}
        </div>
        {!hidden && bookAllowed("resmi") && bookAllowed("imf") && (
          <select
            data-testid="quick-input-book"
            className="rounded-lg border border-slate-700 bg-[#1E293B] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
            value={book}
            onChange={(e) => setBook(e.target.value)}
          >
            <option value="resmi">Kas Resmi</option>
            <option value="imf">Kas IMF</option>
          </select>
        )}
        <input
          data-testid="quick-input-description"
          className="flex-1 min-w-[200px] rounded-lg border border-slate-700 bg-[#1E293B] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
          placeholder="Uraian singkat, mis: Beli kertas HVS"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-slate-400 font-semibold">Rp</span>
          <input
            data-testid="quick-input-amount"
            className="w-40 rounded-lg border border-slate-700 bg-[#1E293B] pl-9 pr-3 py-2 text-sm font-num focus:outline-none focus:ring-2 focus:ring-blue-600"
            inputMode="numeric"
            placeholder="0"
            value={formatNumberInput(amount)}
            onChange={(e) => setAmount(parseDigits(e.target.value))}
          />
        </div>
        <select
          data-testid="quick-input-category"
          className="rounded-lg border border-slate-700 bg-[#1E293B] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
        >
          <option value="">Kategori</option>
          {categories.map((c) => (
            <option key={c.id} value={c.name}>{c.name}</option>
          ))}
        </select>
        <input
          data-testid="quick-input-date"
          type="date"
          className="rounded-lg border border-slate-700 bg-[#1E293B] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
          value={date}
          onChange={(e) => setDate(e.target.value)}
        />
        {type === "expense" && hasPerm("create_special_expense") && (
          <button
            type="button"
            data-testid="quick-input-hidden-toggle"
            onClick={() => setHidden(!hidden)}
            title="Sembunyikan dari Dashboard & Laporan (khusus admin)"
            className={`flex items-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-semibold transition-colors ${
              hidden
                ? "border-amber-500/30 bg-amber-500/10 text-amber-300"
                : "border-slate-700 bg-[#1E293B] text-slate-400 hover:bg-amber-500/10/60"
            }`}
          >
            <EyeOff className="w-3.5 h-3.5" />
            {hidden ? "Tersembunyi" : "Sembunyikan"}
          </button>
        )}
        <button
          data-testid="quick-input-submit"
          type="submit"
          disabled={saving}
          className={`font-medium px-5 py-2 rounded-lg transition-colors flex items-center gap-2 text-sm shadow-sm text-white ${
            type === "income" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-slate-900 hover:bg-slate-800"
          } disabled:opacity-60`}
        >
          {saving && <Loader2 className="w-4 h-4 animate-spin" />}
          Simpan
        </button>
      </form>
    </div>
  );
}
